import React from "react";
//#region types
import {
  AutoCompleteExtraProps,
  EditableTableFieldProps,
  FormFieldDefinitionType,
  FormHandles,
} from "models/propTypes/genericFormProps";
import { VoucherFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
import { DimensionEnums, FormEnums } from "models/enums";
import { FileConstants, FormFieldConstants } from "utils/constants";
import { VoucherCreateFormProps } from "models/propTypes/voucherPropsTypes";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
//#endregion
//#region components
import { GenericForm, TotalAmountTableFooter } from "components/common";
import AccountBalances from "./accountBalances";
import { Grid } from "@mui/material";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";

const VoucherCreateForm: React.ForwardRefRenderFunction<FormHandles, VoucherCreateFormProps> = (props, ref) => {
  const { itemChangeHandler, totalChangeHandler, onItemSelect, onAddNewRow, filterAccounts } = props;
  return (
    <GenericForm<VoucherFormTypes.Create>
      formRef={ref}
      {...props}
      fields={[
        {
          type: FormEnums.FormSectionType.FIELDGROUPSECTION,
          label: TranslationKeys.Common.Attach,
          descriptionText: TranslationKeys.Document.AttachmentHelpText,
          className: clsx(cssExports.voucherFormSection, cssExports.noBorder),
          innerFields: {
            filesToUpload: {
              label: " ",
              type: FormEnums.FormFieldType.FILEUPLOADER,
              extraProps: {
                fileUploaderType: "full",
                filePreviewElementId: FileConstants.DialogFilePreviewPortalId,
                multiSelectionInInstance: true,
              },
            },
            postWithoutImage: {
              label: TranslationKeys.Voucher.PostWithoutImage,
              type: FormEnums.FormFieldType.TOGGLE,
              classes: { formControl: cssExports.inlineLabel },
            },
            date: {
              label: TranslationKeys.Common.Date,
              type: FormEnums.FormFieldType.DATE,
              extraProps: {
                maxDate: new Date(),
              },
            },
            text: {
              label: TranslationKeys.Common.Text,
              type: FormEnums.FormFieldType.TEXT,
            },
          },
        },
        {
          label: TranslationKeys.Common.Transactions,
          type: FormEnums.FormSectionType.FIELDGROUPSECTION,
          className: clsx(cssExports.voucherFormSection, cssExports.noBorder),
          innerFields: {
            accountItems: {
              label: "",
              type: FormEnums.FormFieldType.EDITABLETABLE,
              onChangeHandler: itemChangeHandler,
              className: cssExports.voucherFormSection,
              innerFields: {
                accountName: {
                  label: TranslationKeys.Common.Account,
                  type: FormEnums.FormFieldType.AUTOCOMPLETE,
                  widthColumnCount: 4,
                  placeholder: TranslationKeys.Common.Account,
                  extraProps: {
                    referenceFieldName: FormFieldConstants.Voucher.AccountItem.AccountId,
                    onItemSelect: onItemSelect,
                    items: filterAccounts,
                    itemTemplate: (
                      option: AutocompleteListItem,
                      parts: {
                        text: string;
                        highlight: boolean;
                      }[]
                    ) => (
                      <Grid key={option.id} container className={option.className}>
                        <Grid container wrap="nowrap">
                          <span className={clsx(cssExports.optionTextContainer, option.icon ? cssExports.hasIcon : "")}>
                            <span className={cssExports.optionText}>
                              <span className={clsx(option.description ? cssExports.withSubText : "")}>
                                {option.data}
                              </span>
                            </span>
                            {option.description && <span className={cssExports.subText}>{option.description}</span>}
                          </span>
                        </Grid>
                      </Grid>
                    ),
                  } as AutoCompleteExtraProps<VoucherFormTypes.AccountItem>,
                },
                dimensions: {
                  label: "",
                  type: FormEnums.FormFieldType.DIMENSIONINPUTSELECTOR,
                  extraProps: {
                    type: DimensionEnums.DimensionCategory.CashReconciliation,
                  },
                },
                amount: {
                  label: TranslationKeys.Common.Amount,
                  widthColumnCount: 2,
                  type: FormEnums.FormFieldType.CURRENCY,
                },
              } as FormFieldDefinitionType<VoucherFormTypes.AccountItem>,
              extraProps: {
                isCompactTable: true,
                defaultRow: { accountName: "", accountId: "", dimensions: [], amount: 0 },
                onAddNewRow: onAddNewRow,
                footerRightFields: {
                  totalAmount: {
                    label: "",
                    type: FormEnums.FormFieldType.CUSTOM,
                    onChangeHandler: totalChangeHandler,
                    customField: <TotalAmountTableFooter />,
                  },
                } as FormFieldDefinitionType<VoucherFormTypes.AccountItem>,
              } as EditableTableFieldProps<VoucherFormTypes.AccountItem>,
            },
          },
        },
        {
          type: FormEnums.FormSectionType.FIELDGROUPSECTION,
          label: TranslationKeys.Voucher.AccountBalance,
          className: clsx(cssExports.voucherFormSection, cssExports.noBorder),
          innerFields: {
            accountBalances: {
              label: TranslationKeys.Common.Text,
              type: FormEnums.FormFieldType.CUSTOM,
              customField: <AccountBalances />,
            },
          },
        },
      ]}
      hideSuccess
      formGridLayout={{ message: false }}
    />
  );
};

export default React.forwardRef(VoucherCreateForm);
